// ============================================================
// Campaign Detail — Seed mock data
// ============================================================
//
// Note (2026-06-01): this file is the single source of truth for
// SEED_OPPS. CampaignCatalog/mockData.ts derives its own
// MOCK_OPPORTUNITIES by projecting SEED_OPPS into the thinner
// catalog-row shape — import SEED_OPPS from HERE, not the other
// way around, to avoid a circular-import / TDZ at runtime.
//
// Note (2026-06-12): SEED_OPPS / findOppById are only reachable
// through the mock branches of hooks.ts (USE_REAL_API = false).
// The running app is fully API-backed; these exist as a dev fallback.

import { Opportunity } from './opportunity';

// --- Seed opportunities ---
// Enriched superset of CampaignCatalog MOCK_OPPORTUNITIES with detail-page fields.
// Terminal states (COMPLETED, EXPIRED, CANCELLED, TERMINATED) are stored directly
// as top-level statuses — no date derivation needed.

export const SEED_OPPS: Opportunity[] = [
  // ── UPSIZE ────────────────────────────────────────────────
  {
    id: 'opp-001',
    type: 'UPSIZE',
    status: 'PENDING_APPROVAL',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Upsize Aug 2026',
    dates: '01 Aug – 31 Aug 2026',
    startDate: new Date(2026, 7, 1),
    endDate: new Date(2026, 7, 31),
    submittedAt: '2026-05-06',
    createdByType: 'MERCHANT',
    merchantComment: 'We would love to feature our back-to-school travel campaign during this period.',
    sbEdits: {
      changes: [
        { field: 'Hotels cashback rate', before: '8%', after: '10%' },
        { field: 'Flights cashback rate', before: 'S$1.00', after: 'S$1.28' },
      ],
      note: 'Rates adjusted to meet minimum campaign thresholds for Gold package.',
      editedBy: 'Sarah Tan (ShopBack)',
      editedAt: '2026-05-07T09:30:00+08:00',
    },
    opportunities: [
      {
        package: 'gold',
        startDate: new Date(2026, 7, 1),
        endDate: new Date(2026, 7, 31),
        tiers: [
          { category: 'Flights', baseline: 1.28, rate: 1.28, unit: '$' },
          { category: 'Hotels', baseline: 7.8, rate: 10.0, unit: '%' },
          { category: 'Car Rental', baseline: 7.0, rate: 8.0, unit: '%' },
        ],
      },
    ],
    networkProof: [{ uid: 'np-001-a', name: 'acme_network_proof_may26.pdf', status: 'UPLOADED', url: '#' }],
  },
  // PENDING_APPROVAL (= "Needs your review") test case. Mirrors the
  // post-SB-edit flow: merchant submitted PENDING_SB_REVIEW, ShopBack
  // edited two tier rates + the package, and now the record waits on
  // the merchant's approve/reject decision (STATES.md §4.1 / §6.2).
  // Distinct from opp-001 in brand + dates so both surface in the
  // catalog list and the Pending review tab independently.
  {
    id: 'opp-035',
    type: 'UPSIZE',
    status: 'PENDING_APPROVAL',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Upsize Oct 2026',
    dates: '01 Oct – 31 Oct 2026',
    startDate: new Date(2026, 9, 1),
    endDate: new Date(2026, 9, 31),
    submittedAt: '2026-06-08',
    createdByType: 'MERCHANT',
    merchantComment:
      'Pegging this to our autumn fashion week push — happy to talk through the package fit if rates need adjusting.',
    sbEdits: {
      changes: [
        { field: 'Exposure package', before: 'Silver', after: 'Gold' },
        { field: 'Apparel cashback rate', before: '6%', after: '8%' },
        { field: 'Accessories cashback rate', before: '5%', after: '7%' },
      ],
      note: 'Bumped to Gold so we can promote the autumn drop on the homepage hero. Rates moved up to clear the Gold floor.',
      editedBy: 'Marcus Lee (ShopBack)',
      editedAt: '2026-06-09T11:15:00+08:00',
    },
    opportunities: [
      {
        package: 'gold',
        startDate: new Date(2026, 9, 1),
        endDate: new Date(2026, 9, 31),
        tiers: [
          { category: 'Apparel', baseline: 6.0, rate: 8.0, unit: '%' },
          { category: 'Accessories', baseline: 5.0, rate: 7.0, unit: '%' },
          { category: 'Footwear', baseline: 6.5, rate: 7.5, unit: '%' },
        ],
      },
    ],
    networkProof: [],
  },
  {
    id: 'opp-002',
    type: 'UPSIZE',
    status: 'DRAFT',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Upsize Draft',
    dates: '—',
    startDate: null,
    endDate: null,
    createdByType: 'MERCHANT',
    opportunities: [{ package: null, startDate: null, endDate: null, tiers: [] }],
    networkProof: [],
  },
  {
    id: 'opp-003',
    type: 'UPSIZE',
    status: 'PENDING_SB_REVIEW',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Upsize Jul 2026',
    dates: '15 Jul – 31 Jul 2026',
    startDate: new Date(2026, 6, 15),
    endDate: new Date(2026, 6, 31),
    submittedAt: '2026-05-02',
    createdByType: 'MERCHANT',
    merchantComment: 'Targeting mid-year travel season — aligns with our 7.7 promotion.',
    opportunities: [
      {
        package: 'category-bundle',
        startDate: new Date(2026, 6, 15),
        endDate: new Date(2026, 6, 31),
        tiers: [
          { category: 'Flights', baseline: 1.28, rate: 1.5, unit: '$' },
          { category: 'Hotels', baseline: 7.8, rate: 9.0, unit: '%' },
        ],
      },
    ],
    networkProof: [],
  },
  {
    id: 'opp-004',
    type: 'UPSIZE',
    status: 'APPROVED',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Upsize Sep 2026',
    dates: '01 Sep – 30 Sep 2026',
    startDate: new Date(2026, 8, 1),
    endDate: new Date(2026, 8, 30),
    submittedAt: '2026-04-28',
    createdByType: 'MERCHANT',
    opportunities: [
      {
        package: 'category-bundle',
        startDate: new Date(2026, 8, 1),
        endDate: new Date(2026, 8, 30),
        tiers: [{ category: 'Hotels', baseline: 7.8, rate: 8.5, unit: '%' }],
      },
    ],
    networkProof: [],
  },
  {
    id: 'opp-005',
    type: 'UPSIZE',
    status: 'COMPLETED',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Acme Home & Living — Upsize Mar 2026',
    dates: '01 Mar – 31 Mar 2026',
    startDate: new Date(2026, 2, 1),
    endDate: new Date(2026, 2, 31),
    submittedAt: '2026-02-12',
    createdByType: 'SHOPBACK',
    opportunities: [
      {
        package: 'category-bundle',
        startDate: new Date(2026, 2, 1),
        endDate: new Date(2026, 2, 31),
        tiers: [{ category: 'Hotels', baseline: 7.8, rate: 8.0, unit: '%' }],
      },
    ],
    networkProof: [{ uid: 'np-005-a', name: 'home_living_proof_q1.pdf', status: 'UPLOADED', url: '#' }],
  },
  {
    id: 'opp-007',
    type: 'UPSIZE',
    status: 'REJECTED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Upsize Apr 2026',
    dates: '15 Apr – 30 Apr 2026',
    startDate: new Date(2026, 3, 15),
    endDate: new Date(2026, 3, 30),
    submittedAt: '2026-03-30',
    createdByType: 'MERCHANT',
    rejectionReason:
      'The proposed dates conflict with an existing exclusive agreement with a competing travel brand. Please select alternative dates outside the 14–30 April window.',
    opportunities: [
      {
        package: 'gold',
        startDate: new Date(2026, 3, 15),
        endDate: new Date(2026, 3, 30),
        tiers: [{ category: 'Flights', baseline: 1.28, rate: 2.0, unit: '$' }],
      },
    ],
    networkProof: [],
  },

  // ── AD_SALES ──────────────────────────────────────────────
  {
    id: 'opp-008',
    type: 'AD_SALES',
    status: 'PENDING_APPROVAL',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Ad Sales Jun 2026',
    dates: '08 Jun – 14 Jun 2026',
    startDate: new Date(2026, 5, 8),
    endDate: new Date(2026, 5, 14),
    submittedAt: '2026-05-03',
    createdByType: 'SHOPBACK',
    sbEdits: {
      changes: [
        { field: 'Billing type', before: 'CPM', after: 'CPC' },
        { field: 'PO Number', before: '—', after: 'PO-2026-88421' },
      ],
      note: 'Switched to CPC billing per merchant request and added PO reference.',
      editedBy: 'Nurul Aisyah (ShopBack)',
      editedAt: '2026-05-04T11:15:00+08:00',
    },
    // One opportunity = one package. The wizard supports adding multiple
    // packages at once (bulk-create UX), but submission splits them into
    // separate opportunities, each with its own id and a single `selected`
    // entry. Per UI-fix v2 alignment with the back-end model.
    selected: [
      {
        instanceId: 'ins-008-a',
        packageId: 'category-domination',
        packageName: 'Category Domination',
        packagePrice: 52000,
        durationDays: 30,
        durationLabel: '30 days',
        startDate: new Date(2026, 5, 8),
        endDate: new Date(2026, 6, 7),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201234567A',
      taxNumber: 'M2-0034567-1',
      invoicingAddress: { street: '10 Anson Road', unit: '#20-01', postalCode: '079903' },
    },
    bankAccount: {
      name: 'DBS Operating Account',
      holderName: 'Acme Pte. Ltd.',
      contactEmail: 'finance@acme.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    poNumber: 'PO-2026-88421',
    signedIO: [{ uid: 'io-008-a', name: 'IO_AcmeFashion_JunAdSales.pdf', status: 'UPLOADED', url: '#' }],
  },
  {
    id: 'opp-009',
    type: 'AD_SALES',
    status: 'DRAFT',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Q3 always-on placement',
    dates: '01 Jul – 30 Jul 2026',
    startDate: new Date(2026, 6, 1),
    endDate: new Date(2026, 6, 30),
    createdByType: 'SHOPBACK',
    selected: [
      {
        instanceId: 'ins-009-a',
        packageId: 'category-domination',
        packageName: 'Category Domination',
        packagePrice: 52000,
        durationDays: 30,
        durationLabel: '30 days',
        startDate: new Date(2026, 6, 1),
        endDate: new Date(2026, 6, 30),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Home & Living Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201987654B',
      taxNumber: 'M2-0087654-2',
      invoicingAddress: { street: '1 HarbourFront Avenue', unit: '#04-08', postalCode: '098585' },
    },
    bankAccount: {
      name: 'OCBC Operating Account',
      holderName: 'Acme Home & Living Pte. Ltd.',
      contactEmail: 'finance@acmehome.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    signedIO: [],
  },
  {
    id: 'opp-010',
    type: 'AD_SALES',
    status: 'PENDING_SB_REVIEW',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Ad Sales May 2026',
    dates: '01 May – 07 May 2026',
    startDate: new Date(2026, 4, 1),
    endDate: new Date(2026, 4, 7),
    submittedAt: '2026-04-18',
    createdByType: 'MERCHANT',
    merchantComment: 'Targeting ShopFest weekend — high-intent fashion shoppers.',
    selected: [
      {
        instanceId: 'ins-010-a',
        packageId: 'top-picks-feature',
        packageName: 'Top Picks Feature',
        packagePrice: 7000,
        durationDays: 3,
        durationLabel: '3 days',
        startDate: new Date(2026, 4, 1),
        endDate: new Date(2026, 4, 3),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201234567A',
      taxNumber: 'M2-0034567-1',
      invoicingAddress: { street: '10 Anson Road', unit: '#20-01', postalCode: '079903' },
    },
    bankAccount: {
      name: 'DBS Operating Account',
      holderName: 'Acme Pte. Ltd.',
      contactEmail: 'finance@acme.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    poNumber: 'PO-2026-77391',
    signedIO: [{ uid: 'io-010-a', name: 'IO_AcmeFashion_MayPush.pdf', status: 'UPLOADED', url: '#' }],
  },
  {
    id: 'opp-013',
    type: 'AD_SALES',
    status: 'APPROVED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Ad Sales Jul 2026',
    dates: '01 Jul – 14 Jul 2026',
    startDate: new Date(2026, 6, 1),
    endDate: new Date(2026, 6, 14),
    submittedAt: '2026-05-01',
    createdByType: 'SHOPBACK',
    // One opportunity = one package (see opp-008 note above).
    selected: [
      {
        instanceId: 'ins-013-a',
        packageId: 'brand-takeover',
        packageName: 'Brand Takeover',
        packagePrice: 32000,
        durationDays: 30,
        durationLabel: '30 days',
        startDate: new Date(2026, 6, 1),
        endDate: new Date(2026, 6, 30),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Commerce Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201345678C',
      taxNumber: 'M2-0045678-3',
      invoicingAddress: { street: '3 Temasek Avenue', unit: '#12-00', postalCode: '039190' },
    },
    bankAccount: {
      name: 'OCBC Operating Account',
      holderName: 'Acme Commerce Pte. Ltd.',
      contactEmail: 'finance@acmecommerce.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    poNumber: 'PO-2026-65201',
    signedIO: [{ uid: 'io-013-a', name: 'IO_AcmeCommerce_JulAdSales.pdf', status: 'UPLOADED', url: '#' }],
  },
  {
    id: 'opp-016',
    type: 'AD_SALES',
    status: 'COMPLETED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Ad Sales Feb 2026',
    dates: '01 Feb – 28 Feb 2026',
    startDate: new Date(2026, 1, 1),
    endDate: new Date(2026, 1, 28),
    submittedAt: '2026-01-15',
    createdByType: 'SHOPBACK',
    selected: [
      {
        instanceId: 'ins-016-a',
        packageId: 'gold-booster',
        packageName: 'Gold Booster',
        packagePrice: 750,
        durationDays: 30,
        durationLabel: '30 days',
        startDate: new Date(2026, 1, 1),
        endDate: new Date(2026, 1, 28),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Commerce Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201345678C',
      taxNumber: 'M2-0045678-3',
      invoicingAddress: { street: '3 Temasek Avenue', unit: '#12-00', postalCode: '039190' },
    },
    bankAccount: {
      name: 'OCBC Operating Account',
      holderName: 'Acme Commerce Pte. Ltd.',
      contactEmail: 'finance@acmecommerce.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    poNumber: 'PO-2026-31001',
    signedIO: [{ uid: 'io-016-a', name: 'IO_AcmeCommerce_FebAdSales.pdf', status: 'UPLOADED', url: '#' }],
  },
  {
    id: 'opp-019',
    type: 'AD_SALES',
    status: 'REJECTED',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Acme Home & Living — Ad Sales Mar 2026',
    dates: '15 Mar – 31 Mar 2026',
    startDate: new Date(2026, 2, 15),
    endDate: new Date(2026, 2, 31),
    submittedAt: '2026-03-01',
    createdByType: 'SHOPBACK',
    rejectionReason:
      'Insertion order could not be verified — the uploaded document appears unsigned. Please resubmit with a countersigned IO.',
    selected: [
      {
        instanceId: 'ins-019-a',
        packageId: 'top-picks-feature',
        packageName: 'Top Picks Feature',
        packagePrice: 7000,
        durationDays: 3,
        durationLabel: '3 days',
        startDate: new Date(2026, 2, 15),
        endDate: new Date(2026, 2, 17),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Home & Living Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201987654B',
      taxNumber: 'M2-0087654-2',
      invoicingAddress: { street: '1 HarbourFront Avenue', unit: '#04-08', postalCode: '098585' },
    },
    bankAccount: {
      name: 'OCBC Operating Account',
      holderName: 'Acme Home & Living Pte. Ltd.',
      contactEmail: 'finance@acmehome.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    signedIO: [{ uid: 'io-019-a', name: 'IO_AcmeHomeL_Mar_unsigned.pdf', status: 'PENDING', url: '#' }],
  },

  // ── STANDALONE_OFFER ──────────────────────────────────────
  {
    id: 'opp-so-001',
    type: 'STANDALONE_OFFER',
    status: 'APPROVED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Standalone Offer — Free shipping on S$50+',
    dates: '20 May – 30 Jun 2026',
    startDate: new Date(2026, 4, 20),
    endDate: new Date(2026, 5, 30),
    submittedAt: '2026-04-30',
    createdByType: 'MERCHANT',
    title: 'Free shipping on orders S$50+',
    descriptionLong: 'Use code SHIPFREE at checkout. Valid on all Acme Commerce orders S$50 and above. T&Cs apply.',
    image: 'https://placehold.co/600x400?text=Acme+Commerce',
    landingUrl: 'https://acmecommerce.sg/shopback-free-shipping',
    linkedOpportunityId: 'opp-006',
  },
  {
    id: 'opp-so-002',
    type: 'STANDALONE_OFFER',
    status: 'PENDING_SB_REVIEW',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Standalone Offer — 10% off sitewide',
    dates: '01 Jun – 15 Jun 2026',
    startDate: new Date(2026, 5, 1),
    endDate: new Date(2026, 5, 15),
    submittedAt: '2026-05-05',
    createdByType: 'MERCHANT',
    merchantComment: 'Ahead of the mid-year sale — want to drive urgency with a sitewide discount.',
    title: '10% off sitewide — exclusive for ShopBack',
    descriptionLong: 'Enjoy 10% off all fashion categories at Acme Fashion, exclusively via ShopBack. No code needed.',
    image: 'https://placehold.co/600x400?text=Acme+Fashion',
    landingUrl: 'https://acmefashion.sg/shopback-exclusive',
  },
  {
    id: 'opp-so-003',
    type: 'STANDALONE_OFFER',
    status: 'DRAFT',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Standalone Offer — Summer home décor deal (draft)',
    dates: '—',
    startDate: null,
    endDate: null,
    createdByType: 'MERCHANT',
    title: '',
    descriptionLong: '',
    landingUrl: '',
  },
  {
    id: 'opp-so-004',
    type: 'STANDALONE_OFFER',
    status: 'REJECTED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Standalone Offer — Flash deal 20% off',
    dates: '01 Apr – 07 Apr 2026',
    startDate: new Date(2026, 3, 1),
    endDate: new Date(2026, 3, 7),
    submittedAt: '2026-03-25',
    createdByType: 'MERCHANT',
    rejectionReason:
      'Landing URL returned a 404 error during verification. Please ensure the promotion page is live before resubmitting.',
    title: 'Flash deal — 20% off all electronics',
    descriptionLong: 'One-week flash deal. 20% off all electronics at Acme Commerce via ShopBack link.',
    image: 'https://placehold.co/600x400?text=Acme+Flash',
    landingUrl: 'https://acmecommerce.sg/flash-april',
  },
  {
    id: 'opp-so-005',
    type: 'STANDALONE_OFFER',
    status: 'COMPLETED',
    brand: 'Acme Fashion',
    country: 'SG',
    description: "Standalone Offer — Valentine's Day special",
    dates: '10 Feb – 14 Feb 2026',
    startDate: new Date(2026, 1, 10),
    endDate: new Date(2026, 1, 14),
    submittedAt: '2026-01-20',
    createdByType: 'MERCHANT',
    title: "Valentine's Day — 15% off selected items",
    descriptionLong: "Treat your loved ones. 15% off selected fashion items at Acme Fashion for Valentine's Day.",
    image: 'https://placehold.co/600x400?text=Valentine',
    landingUrl: 'https://acmefashion.sg/valentines',
  },
  // STANDALONE_OFFER × PENDING_APPROVAL
  {
    id: 'opp-so-006',
    type: 'STANDALONE_OFFER',
    status: 'PENDING_APPROVAL',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Standalone Offer — Summer clearance 30% off',
    dates: '15 Jun – 30 Jun 2026',
    startDate: new Date(2026, 5, 15),
    endDate: new Date(2026, 5, 30),
    submittedAt: '2026-05-02',
    createdByType: 'MERCHANT',
    sbEdits: {
      changes: [
        { field: 'Description', before: '30% off all summer items', after: '30% off selected summer items' },
        {
          field: 'Landing URL',
          before: 'https://acmefashion.sg/summer',
          after: 'https://acmefashion.sg/summer-clearance',
        },
      ],
      note: 'Narrowed scope to "selected items" per brand guidelines. Updated landing URL to the correct campaign page.',
      editedBy: 'Sarah Chen (ShopBack)',
      editedAt: '2026-05-06T14:30:00+08:00',
    },
    title: 'Summer clearance — 30% off selected items',
    descriptionLong: 'End-of-season clearance. 30% off selected summer fashion at Acme Fashion.',
    image: 'https://placehold.co/600x400?text=Summer+Sale',
    landingUrl: 'https://acmefashion.sg/summer-clearance',
  },

  // ── TERMINAL STATES ───────────────────────────────────────
  {
    id: 'opp-expired-001',
    type: 'UPSIZE',
    status: 'EXPIRED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Upsize Nov 2025 (Expired)',
    dates: '01 Nov – 30 Nov 2025',
    startDate: new Date(2025, 10, 1),
    endDate: new Date(2025, 10, 30),
    submittedAt: '2025-10-01',
    createdByType: 'MERCHANT',
    // Backfilled 2026-06-01: terminal-state metadata per STATES.md §1.5.
    // The auto-transition fires on the start date when the record never
    // reached APPROVED; here that's 2025-11-01.
    expiredAt: '2025-11-01T00:00:00+08:00',
    opportunities: [
      {
        package: 'category-bundle',
        startDate: new Date(2025, 10, 1),
        endDate: new Date(2025, 10, 30),
        tiers: [{ category: 'Hotels', baseline: 7.8, rate: 8.5, unit: '%' }],
      },
    ],
    networkProof: [],
  },
  {
    id: 'opp-cancelled-001',
    type: 'AD_SALES',
    status: 'CANCELLED',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Ad Sales Dec 2025 (Cancelled)',
    dates: '01 Dec – 31 Dec 2025',
    startDate: new Date(2025, 11, 1),
    endDate: new Date(2025, 11, 31),
    submittedAt: '2025-11-10',
    createdByType: 'MERCHANT',
    // Backfilled 2026-06-01: terminal-state metadata per STATES.md §1.5.
    // Merchant-triggered Cancel before approval.
    cancelledAt: '2025-11-15T14:22:00+08:00',
    cancelledBy: 'MERCHANT',
    cancelReason: 'Budget reallocated to Q1 brand activation.',
    selected: [
      {
        instanceId: 'ins-cancelled-a',
        packageId: 'top-picks-feature',
        packageName: 'Top Picks Feature',
        packagePrice: 7000,
        durationDays: 3,
        durationLabel: '3 days',
        startDate: new Date(2025, 11, 1),
        endDate: new Date(2025, 11, 3),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201234567A',
      taxNumber: 'M2-0034567-1',
      invoicingAddress: { street: '10 Anson Road', unit: '#20-01', postalCode: '079903' },
    },
    bankAccount: {
      name: 'DBS Operating Account',
      holderName: 'Acme Pte. Ltd.',
      contactEmail: 'finance@acme.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    signedIO: [],
  },
  {
    id: 'opp-terminated-001',
    type: 'UPSIZE',
    status: 'TERMINATED',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Acme Home & Living — Upsize Feb 2026 (Terminated)',
    dates: '01 Feb – 28 Feb 2026',
    startDate: new Date(2026, 1, 1),
    endDate: new Date(2026, 1, 28),
    submittedAt: '2026-01-05',
    createdByType: 'MERCHANT',
    // Backfilled 2026-06-01: terminal-state metadata per STATES.md §1.5.
    // Merchant-triggered Terminate after the record was APPROVED.
    terminatedAt: '2026-02-15T11:08:00+08:00',
    terminatedBy: 'MERCHANT',
    terminateReason: 'Hotel partner agreement ended ahead of schedule.',
    opportunities: [
      {
        package: 'gold',
        startDate: new Date(2026, 1, 1),
        endDate: new Date(2026, 1, 28),
        tiers: [{ category: 'Hotels', baseline: 7.8, rate: 10.0, unit: '%' }],
      },
    ],
    networkProof: [],
  },

  // ── Coverage backfill (2026-06-01) ────────────────────────────
  // SEED_OPPS used to be missing six type × status combinations the
  // catalog list surfaces. Without these entries, clicking a catalog
  // row in any of these states 404'd because `findOppById()` reads
  // from SEED_OPPS. Each row carries the terminal metadata per
  // STATES.md §1.5 + the type-specific payload the detail page reads
  // (selected[] for AD_SALES, opportunities[] for UPSIZE, title +
  // descriptionLong for STANDALONE_OFFER).

  // — UPSIZE / CANCELLED — merchant cancelled before approval
  {
    id: 'opp-026',
    type: 'UPSIZE',
    status: 'CANCELLED',
    brand: 'Acme Fashion',
    country: 'SG',
    description: 'Acme Fashion — Upsize Apr 2026 (Cancelled)',
    dates: '01 Apr – 30 Apr 2026',
    startDate: new Date(2026, 3, 1),
    endDate: new Date(2026, 3, 30),
    submittedAt: '2026-03-22',
    createdByType: 'MERCHANT',
    cancelledAt: '2026-03-25T10:00:00+08:00',
    cancelledBy: 'MERCHANT',
    cancelReason: 'Decided to bundle with the Q2 affiliate launch instead.',
    opportunities: [
      {
        package: 'silver',
        startDate: new Date(2026, 3, 1),
        endDate: new Date(2026, 3, 30),
        tiers: [{ category: 'Hotels', baseline: 7.8, rate: 8.5, unit: '%' }],
      },
    ],
    networkProof: [],
  },

  // — AD_SALES / EXPIRED — submission never reached approval before start
  {
    id: 'opp-027',
    type: 'AD_SALES',
    status: 'EXPIRED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Acme Commerce — Ad Sales Mar 2026 (Expired)',
    dates: '15 Mar – 15 Apr 2026',
    startDate: new Date(2026, 2, 15),
    endDate: new Date(2026, 3, 15),
    submittedAt: '2026-03-01',
    createdByType: 'MERCHANT',
    expiredAt: '2026-03-15T00:00:00+08:00',
    expireReason: 'Auto-expired — submission did not reach approval before the start date.',
    selected: [
      {
        instanceId: 'ins-expired-a',
        packageId: 'brand-boost',
        packageName: 'Brand Boost',
        packagePrice: 20000,
        durationDays: 7,
        durationLabel: '7 days',
        startDate: new Date(2026, 2, 15),
        endDate: new Date(2026, 2, 21),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201234567A',
      taxNumber: 'M2-0034567-1',
      invoicingAddress: { street: '10 Anson Road', unit: '#20-01', postalCode: '079903' },
    },
    bankAccount: {
      name: 'DBS Operating Account',
      holderName: 'Acme Pte. Ltd.',
      contactEmail: 'finance@acme.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    signedIO: [],
  },

  // — AD_SALES / TERMINATED — approved booking terminated after launch
  {
    id: 'opp-028',
    type: 'AD_SALES',
    status: 'TERMINATED',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Acme Home & Living — Ad Sales Apr 2026 (Terminated)',
    dates: '10 Apr – 30 Apr 2026',
    startDate: new Date(2026, 3, 10),
    endDate: new Date(2026, 3, 30),
    submittedAt: '2026-03-28',
    createdByType: 'MERCHANT',
    terminatedAt: '2026-04-18T09:42:00+08:00',
    terminatedBy: 'MERCHANT',
    terminateReason: 'Creative no longer aligned with brand refresh — relaunching in Q3.',
    selected: [
      {
        instanceId: 'ins-terminated-a',
        packageId: 'top-picks-feature',
        packageName: 'Top Picks Feature',
        packagePrice: 7000,
        durationDays: 3,
        durationLabel: '3 days',
        startDate: new Date(2026, 3, 10),
        endDate: new Date(2026, 3, 12),
        acknowledgedSoldOut: false,
      },
    ],
    billingType: 'Direct',
    legalEntity: {
      accountName: 'Acme Pte. Ltd.',
      country: 'SG',
      companyRegNumber: '201234567A',
      taxNumber: 'M2-0034567-1',
      invoicingAddress: { street: '10 Anson Road', unit: '#20-01', postalCode: '079903' },
    },
    bankAccount: {
      name: 'DBS Operating Account',
      holderName: 'Acme Pte. Ltd.',
      contactEmail: 'finance@acme.com',
      billingCurrency: 'SGD',
      billingSameAsInvoicing: true,
      mailingSameAsBilling: true,
    },
    signedIO: [{ uid: 'io-028', name: 'IO_opp-028_signed.pdf', status: 'SIGNED', url: '#' }],
  },

  // — STANDALONE_OFFER / EXPIRED —
  {
    id: 'opp-033',
    type: 'STANDALONE_OFFER',
    status: 'EXPIRED',
    brand: 'Acme Fashion',
    country: 'SG',
    description: '10% off Acme Fashion summer essentials, valid storewide for 2 weeks.',
    title: 'Summer Essentials — 10% off',
    dates: '05 Apr – 20 Apr 2026',
    startDate: new Date(2026, 3, 5),
    endDate: new Date(2026, 3, 20),
    submittedAt: '2026-03-25',
    createdByType: 'MERCHANT',
    expiredAt: '2026-04-05T00:00:00+08:00',
    expireReason: 'Auto-expired — submission did not reach approval before the offer start date.',
    descriptionLong:
      "Save 10% on Acme Fashion's summer essentials line — light layers, breathable knits, " +
      'and resort wear. Auto-applied at checkout on the ShopBack app.',
    landingUrl: 'https://acmefashion.example.com/summer-essentials',
  },

  // — STANDALONE_OFFER / CANCELLED — merchant withdrew before approval
  {
    id: 'opp-029-cancelled',
    type: 'STANDALONE_OFFER',
    status: 'CANCELLED',
    brand: 'Acme Home & Living',
    country: 'SG',
    description: 'Free shipping on Acme Home & Living orders over SGD 80 for 10 days.',
    title: 'Free shipping over SGD 80',
    dates: '15 Feb – 28 Feb 2026',
    startDate: new Date(2026, 1, 15),
    endDate: new Date(2026, 1, 28),
    submittedAt: '2026-01-20',
    createdByType: 'SHOPBACK',
    cancelledAt: '2026-01-25T14:15:00+08:00',
    cancelledBy: 'MERCHANT',
    cancelReason: 'Logistics capacity reduced — cannot honour free shipping commitment for this window.',
    descriptionLong:
      'Spend SGD 80 or more on Acme Home & Living and enjoy free standard shipping. ' +
      'Limit one redemption per shopper per day.',
    landingUrl: 'https://acmehomeliving.example.com/free-shipping',
  },

  // — STANDALONE_OFFER / TERMINATED — approved offer ended early
  {
    id: 'opp-034',
    type: 'STANDALONE_OFFER',
    status: 'TERMINATED',
    brand: 'Acme Commerce',
    country: 'SG',
    description: 'Spend SGD 100 get SGD 15 bonus cashback at Acme Commerce, valid for 30 days.',
    title: 'Spend SGD 100, get SGD 15 bonus',
    dates: '10 Mar – 10 Apr 2026',
    startDate: new Date(2026, 2, 10),
    endDate: new Date(2026, 3, 10),
    submittedAt: '2026-03-02',
    createdByType: 'MERCHANT',
    terminatedAt: '2026-03-22T16:30:00+08:00',
    terminatedBy: 'SHOPBACK',
    terminateReason: 'Promotion budget exhausted ahead of schedule.',
    descriptionLong:
      'Spend SGD 100 or more in a single transaction and earn SGD 15 bonus cashback on top of the base rate. ' +
      'Bonus paid out within 30 days of confirmation.',
    landingUrl: 'https://acmecommerce.example.com/bonus-cashback',
  },
];

export { SEED_OPPS as MOCK_OPPORTUNITIES };

// --- Lookup helper ---

export function findOppById(id: string): Opportunity | undefined {
  return SEED_OPPS.find((o) => o.id === id);
}
